import { Body, Header, IndexScroll } from "components";
import ThumbnailCardViewGridAbout from "components/ThumbnailCardViewGridAbout";
import Section from "components/layout/Section";
import { featuredWorks } from "module/featuredWorks";
import { setPageIndex } from "module/reducers/pageIndexReducer";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logAnalyticsEvent } from "api/firebaseManager";
import Footer from "components/layout/Footer";
import "sass/views/AboutContainer.scss";
import SEO from "../components/SEO";

type AboutSectionType = "links" | "featuredProject" | "skillStack" | "onGoing";

function AboutContainer() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const sectionRefs = useRef<HTMLDivElement[] | null[]>([]);
  const [showToast, setShowToast] = useState(false);
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState<"core" | "tech">("core");
  const [activeSection, setActiveSection] = useState<AboutSectionType>("links");

  const onClickMail = async () => {
    try {
      await navigator.clipboard.writeText("tkahckd@gmail.com");
      logAnalyticsEvent("contact_click", {
        type: "email",
      });
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000); // hide toast after 2s
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  useEffect(() => {
    dispatch(setPageIndex(1));

    // About 페이지 진입 이벤트 로깅
    logAnalyticsEvent("page_view", {
      page_name: "about",
    });
  }, [dispatch]);

  const handleFeaturedWorkClick = (itemKey: string, title: string) => {
    logAnalyticsEvent("featured_work_click", {
      work_key: itemKey,
      work_title: title,
    });
  };

  const handleLinkClick = (
    productId: string,
    linkName: string,
    url: string
  ) => {
    logAnalyticsEvent("link_click", {
      product_id: productId,
      link_name: linkName,
      link_url: url,
    });
  };

  return (
    <>
      <SEO
        title={i18n.language === "ko" ? "소개 | Lingple" : "About | Lingple"}
        description={
          i18n.language === "ko"
            ? "Lingple 제작자 소개"
            : "About the creator of Lingple"
        }
        keywords={
          i18n.language === "ko"
            ? "Lingple, 소개, 플랫폼, 게임, 도구, 멀티플레이, 오프라인"
            : "Lingple, about, platform, games, tools, multiplayer, offline"
        }
      />
      <Header />
      <Body
        indexScroll={
          <IndexScroll sectionTitles={["About"]} sectionRefs={sectionRefs} />
        }
      >
        <div
          className="about-container space-gray-theme"
          ref={(itself) => (sectionRefs.current[0] = itself)}
        >
          <Section isFirst name={"about-intro"}>
            <div className="flex items-end mb-6 gap-4">
              <div className="text-gray-400 font-medium text-xl">dev. by</div>
              <div className="font-semibold text-4xl">Jay Shin</div>
            </div>
            {/* <div className="text-gray-300 md:text-lg mb-6">
              <div>{t("About.intro.0")}</div>
              <div>{t("About.intro.1")}</div>
              <div>{t("About.intro.2")}</div>
              <div>{t("About.intro.3")}</div>
            </div> */}
            <div className="mt-12 font-semibold text-lg text-gray-300">
              {t("contact")}
            </div>
            <div className="flex md:flex-row flex-col md:gap-16 gap-4 py-2">
              <div
                className="flex items-center cursor-pointer transition-all duration-300 hover:transform hover:-translate-y-0.5 contact-hover"
                onClick={onClickMail}
              >
                <i className="far fa-envelope text-xl mr-2 transition-colors duration-300" />
                <div className="transition-colors duration-300">
                  tkahckd@gmail.com
                </div>
              </div>
              <a
                className="flex items-center cursor-pointer transition-all duration-300 hover:transform hover:-translate-y-0.5 contact-hover"
                href="https://www.linkedin.com/in/jay-shin-45a4ba2ab/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  logAnalyticsEvent("contact_click", {
                    type: "linkedin",
                  })
                }
              >
                <i className="fab fa-linkedin text-xl mr-2 transition-colors duration-300" />
                <div className="transition-colors duration-300">Jay Shin</div>
              </a>
              <a
                className="flex items-center cursor-pointer transition-all duration-300 hover:transform hover:-translate-y-0.5 contact-hover"
                href="https://www.instagram.com/_js_shin_/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  logAnalyticsEvent("contact_click", {
                    type: "instagram",
                  })
                } // 추가
              >
                <i className="fab fa-instagram text-xl mr-2 transition-colors duration-300" />
                <div className="transition-colors duration-300">@_js_shin_</div>
              </a>
              <a
                className="flex items-center cursor-pointer transition-all duration-300 hover:transform hover:-translate-y-0.5 contact-hover"
                href="https://x.com/HvDdKiBJKi18163"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  logAnalyticsEvent("contact_click", {
                    type: "x",
                  })
                } // 추가
              >
                <i className="fab fa-x-twitter text-xl mr-2 transition-colors duration-300" />
                <div className="transition-colors duration-300">Jay Shin</div>
              </a>
            </div>
            {/* Quick Navigation */}
            <div className="mt-12 flex flex-wrap gap-3">
              <button
                onClick={() => setActiveSection("links")}
                className={`px-4 py-2 ${
                  activeSection === "links"
                    ? "bg-gray-600 ring-2 ring-gray-400"
                    : "bg-gray-700 hover:bg-gray-600"
                } rounded-md text-sm font-medium transition-colors cursor-pointer !rounded-button whitespace-nowrap`}
              >
                <i className="fas fa-globe mr-2"></i>
                {t("services")}
              </button>
              <button
                onClick={() => setActiveSection("featuredProject")}
                className={`px-4 py-2 ${
                  activeSection === "featuredProject"
                    ? "bg-gray-600 ring-2 ring-gray-400"
                    : "bg-gray-700 hover:bg-gray-600"
                } rounded-md text-sm font-medium transition-colors cursor-pointer !rounded-button whitespace-nowrap`}
              >
                <i className="fas fa-star mr-2"></i>
                {t("featuredWork")}
              </button>
              <button
                onClick={() => setActiveSection("onGoing")}
                className={`px-4 py-2 ${
                  activeSection === "onGoing"
                    ? "bg-gray-600 ring-2 ring-gray-400"
                    : "bg-gray-700 hover:bg-gray-600"
                } rounded-md text-sm font-medium transition-colors cursor-pointer !rounded-button whitespace-nowrap`}
              >
                <i className="fas fa-tasks mr-2"></i>
                {t("currentProject")}
              </button>
              <button
                onClick={() => setActiveSection("skillStack")}
                className={`px-4 py-2 ${
                  activeSection === "skillStack"
                    ? "bg-gray-600 ring-2 ring-gray-400"
                    : "bg-gray-700 hover:bg-gray-600"
                } rounded-md text-sm font-medium transition-colors cursor-pointer !rounded-button whitespace-nowrap`}
              >
                <i className="fas fa-code mr-2"></i>
                {t("skillStack")}
              </button>
              {/* <button
                onClick={() => setActiveSection("experience")}
                className={`px-4 py-2 ${
                  activeSection === "experience"
                    ? "bg-indigo-700 ring-2 ring-indigo-300"
                    : "bg-indigo-600 hover:bg-indigo-700"
                } rounded-md text-sm font-medium transition-colors cursor-pointer !rounded-button whitespace-nowrap`}
              >
                <i className="fas fa-briefcase mr-2"></i>Experience
              </button> */}

              {/* <button
                onClick={() => setActiveSection("education")}
                className={`px-4 py-2 ${
                  activeSection === "education"
                    ? "bg-indigo-700 ring-2 ring-indigo-300"
                    : "bg-indigo-600 hover:bg-indigo-700"
                } rounded-md text-sm font-medium transition-colors cursor-pointer !rounded-button whitespace-nowrap`}
              >
                <i className="fas fa-graduation-cap mr-2"></i>Education
              </button> */}
            </div>
          </Section>
          <section className="min-h-[600px]">
            {/* Links Section */}
            {activeSection === "links" && (
              <Section name={"about-links"}>
                <div className="space-y-8">
                  {/* Lingple */}
                  <div className="bg-gradient-to-br from-[#1a1f2e] to-[#1e2438] rounded-lg p-6 border border-gray-800">
                    <div className="flex items-center gap-4 mb-4">
                      <img
                        src="/img/ic_logo_lingple.png"
                        alt="Lingple logo"
                        className="w-16 h-16 rounded-lg"
                        onError={(e) => {
                          e.currentTarget.src = "/img/logo512.png";
                        }}
                      />
                      <div>
                        <h3 className="text-xl font-semibold text-white">
                          Lingple
                        </h3>
                        <p className="text-gray-400 text-sm">
                          {t("ProductLinks.services.lingple.subtitle")}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <a
                        href="/"
                        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-md text-sm font-medium transition-colors cursor-pointer"
                        onClick={() => handleLinkClick("lingple", "home", "/")}
                      >
                        <i className="fas fa-home"></i>
                        <span>
                          {t("ProductLinks.services.lingple.links.home")}
                        </span>
                      </a>
                      {/* <a
                        href="/about"
                        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-md text-sm font-medium transition-colors cursor-pointer"
                        onClick={() =>
                          handleLinkClick("lingple", "about", "/about")
                        }
                      >
                        <i className="fas fa-user"></i>
                        <span>
                          {t("ProductLinks.services.lingple.links.about")}
                        </span>
                      </a> */}
                      {/* <a
                        href="/about"
                        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-md text-sm font-medium transition-colors cursor-pointer"
                        onClick={() =>
                          handleLinkClick("lingple", "portfolio", "/about")
                        }
                      >
                        <i className="fas fa-briefcase"></i>
                        <span>
                          {t("ProductLinks.services.lingple.links.portfolio")}
                        </span>
                      </a> */}
                    </div>
                  </div>

                  {/* Guham */}
                  <div className="bg-gradient-to-br from-[#1a1f2e] to-[#1e2438] rounded-lg p-6 border border-gray-800">
                    <div className="flex items-center gap-4 mb-4">
                      <img
                        src="/img/ic_logo_guham.png"
                        alt="Guham logo"
                        className="w-16 h-16 rounded-lg"
                        onError={(e) => {
                          e.currentTarget.src = "/img/logo512.png";
                        }}
                      />
                      <div>
                        <h3 className="text-xl font-semibold text-white">
                          {t("ProductLinks.services.guham.name")}
                        </h3>
                        <p className="text-gray-400 text-sm">
                          {t("ProductLinks.services.guham.subtitle")}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <a
                        href="https://guham.net"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-md text-sm font-medium transition-colors cursor-pointer"
                        onClick={() =>
                          handleLinkClick("guham", "web", "https://guham.net")
                        }
                      >
                        <i className="fas fa-globe"></i>
                        <span>
                          {t("ProductLinks.services.guham.links.web")}
                        </span>
                        <i className="fas fa-external-link-alt text-xs"></i>
                      </a>
                      <a
                        href="https://guham.net/app"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-md text-sm font-medium transition-colors cursor-pointer"
                        onClick={() =>
                          handleLinkClick("guham", "app", "https://guham.net/app")
                        }
                      >
                        <i className="fas fa-mobile-alt"></i>
                        <span>
                          {t("ProductLinks.services.guham.links.app")}
                        </span>
                        <i className="fas fa-external-link-alt text-xs"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </Section>
            )}
            {/* Featured Projects */}
            {activeSection === "featuredProject" && (
              <Section name={"about-work"}>
                {/* <h2 className="text-indigo-400 text-xl font-semibold mb-8">
                  Ongoing Projects
                </h2> */}
                <ThumbnailCardViewGridAbout
                  items={featuredWorks}
                  onItemClick={handleFeaturedWorkClick}
                ></ThumbnailCardViewGridAbout>
              </Section>
            )}
            {/* Ongoing Projects */}
            {activeSection === "onGoing" && (
              <Section name="about-work">
                {/* <h2 className="text-indigo-400 text-xl font-semibold mb-8">
                  Ongoing Projects
                </h2> */}
                <div className="flex flex-col md:flex-row gap-8 text-white">
                  {/* Ongoing Project 1 */}
                  <div className="w-full md:w-1/2 h-fit bg-gradient-to-br from-[#1a1f2e] to-[#1e2438] rounded-lg p-6 border border-gray-800">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-lg font-medium">Lingple</h3>
                      {/* <span className="px-3 py-1 bg-indigo-900/50 text-indigo-300 text-xs rounded-full">
                        70% Complete
                      </span> */}
                    </div>
                    <p className="text-gray-300 mb-4">
                      {t("About.onGoing.lingtopleDesc")}
                    </p>
                    {/* <div className="mb-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-400">{t("progress")}</span>
                        <span className="text-gray-400">20%</span>
                      </div>
                      <div className="h-2 w-full bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-indigo-500 rounded-full"
                          style={{ width: "20%" }}
                        ></div>
                      </div>
                    </div> */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {/* <span className="px-2 py-1 bg-gray-700 text-xs rounded-full">
                        React
                      </span> */}
                    </div>
                    {/* <div className="text-sm text-gray-400">
                      <i className="far fa-calendar-alt mr-2"></i>
                      Expected completion: August 2025
                    </div> */}
                  </div>
                  {/* Ongoing Project 2 */}
                  <div className="h-fit min-w-80 bg-gradient-to-br from-[#1a1f2e] to-[#1e2438] rounded-lg p-6 border border-gray-800">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-lg font-medium">
                        {t("About.onGoing.inGraceTitle")}
                      </h3>
                      {/* <span className="px-3 py-1 bg-indigo-900/50 text-indigo-300 text-xs rounded-full">
                        40% Complete
                      </span> */}
                    </div>
                    <p className="text-gray-300 mb-4 cursor-pointer">
                      <a className="cursor-pointer" href="https://guham.net">
                        {t("About.onGoing.inGraceDesc")}
                      </a>
                    </p>
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-400">Progress</span>
                        <span className="text-gray-400">80%</span>
                      </div>
                      <div className="h-2 w-full bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gray-400 rounded-full"
                          style={{ width: "80%" }}
                        ></div>
                      </div>
                    </div>
                    {/* <div className="flex flex-wrap gap-2 mb-4">
                      <span className="px-2 py-1 bg-gray-700 text-xs rounded-full">
                        Flutter
                      </span>
                      <span className="px-2 py-1 bg-gray-700 text-xs rounded-full">
                        Supabase
                      </span>
                    </div> */}
                    {/* <div className="text-sm text-gray-400">
                      <i className="far fa-calendar-alt mr-2"></i>
                      Expected completion: December 2025
                    </div> */}
                  </div>
                </div>
              </Section>
            )}
            {activeSection === "skillStack" && (
              <Section name={"about-work"}>
                {/* <h2 className="text-indigo-400 text-xl font-semibold mb-8">
                  Skill Stack
                </h2> */}
                {/* Tabs */}
                <div className="flex gap-4 mb-8">
                  <button
                    onClick={() => setActiveTab("core")}
                    className={`whitespace-nowrap px-4 py-2 rounded-md cursor-pointer ${
                      activeTab === "core"
                        ? "bg-gray-600 text-white"
                        : "bg-gray-700 text-gray-300"
                    }`}
                  >
                    {t("coreSkill")}
                  </button>
                  <button
                    onClick={() => setActiveTab("tech")}
                    className={`px-4 py-2 cursor-pointer rounded-md ${
                      activeTab === "tech"
                        ? "bg-gray-600 text-white"
                        : "bg-gray-700 text-gray-300"
                    }`}
                  >
                    {`${t("programming")} & ${t("others")}`}
                  </button>
                </div>

                {activeTab === "core" ? (
                  // Core Skills
                  <div className="mb-12 text-white space-y-8">
                    {[
                      {
                        icon: "fa-solid fa-terminal",
                        title: "About.title.noAI",
                        desc: [
                          "About.desc.selfTaught",
                          "About.desc.multipleDevelop",
                          "About.desc.designSkill",
                          "About.desc.ideaToImplementation",
                        ],
                      },
                      // {
                      //   icon: "fa-solid fa-brain",
                      //   title: "About.title.withAI",
                      //   desc: [
                      //     "About.desc.amplifyWithAI",
                      //     "About.desc.aiCodeReview",
                      //   ],
                      // },
                      {
                        icon: "fab fa-apple",
                        title: "About.title.ios",
                        desc: [
                          "About.desc.iosLeadDev",
                          "About.desc.swiftExperience",
                        ],
                      },
                      {
                        icon: "fab fa-flutter",
                        title: "About.title.flutter",
                        desc: [
                          "About.desc.flutterLeadProject",
                          "About.desc.flutterNativePerf",
                          "About.desc.flutterWebFirst",
                        ],
                      },
                      {
                        icon: "fab fa-react",
                        title: "About.title.react",
                        desc: [
                          "About.desc.reactFullstack",
                          "About.desc.reactStyling",
                          "About.desc.reactSocket",
                          "About.desc.reactBackend",
                        ],
                      },
                    ].map((item, index) => (
                      <div key={index} className="bg-[#1a1f2e] p-6 rounded-lg">
                        <div className="flex items-center mb-3">
                          <i className={`${item.icon} text-gray-400 mr-3`}></i>
                          <h4 className="text-lg font-medium">
                            {t(item.title)}
                          </h4>
                        </div>
                        {item.desc.map((item, index) => (
                          <div key={index} className="flex gap-2">
                            •
                            <p key={index} className="text-gray-300">
                              {t(item)}
                            </p>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ) : (
                  // Programming & Frameworks
                  <div className="text-white">
                    {/* Programming Languages */}
                    <div>
                      <h3 className="text-lg font-medium mb-6">
                        {t("programming")}
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-12 gap-y-4">
                        {[
                          { name: "Swift", level: 90, icon: "fab fa-apple" },
                          { name: "SwiftUI", level: 70, icon: "fab fa-apple" },
                          {
                            name: "Java",
                            level: 90,
                            icon: "fa-brands fa-android",
                          },
                          {
                            name: "Kotlin",
                            level: 80,
                            icon: "fa-brands fa-android",
                          },
                          {
                            name: "Flutter",
                            level: 80,
                            icon: "fas fa-mobile-alt",
                          },

                          {
                            name: "React",
                            level: 80,
                            icon: "fab fa-react",
                          },
                          { name: "JavaScript", level: 80 },
                          { name: "TypeScript", level: 90 },
                          {
                            name: "Unity",
                            level: 90,
                            icon: "fa-gamepad",
                          },
                          {
                            name: "Socket Server",
                            level: 90,
                            icon: "fa-server",
                          },
                          {
                            name: "ASP.Net",
                            level: 70,
                            icon: "fa-server",
                          },
                          {
                            name: "Node.js",
                            level: 70,
                            icon: "fa-brands fa-node-js",
                          },
                          {
                            name: "Ubuntu",
                            level: 70,
                            icon: "fa-server",
                          },
                          {
                            name: "Spring Boot",
                            level: 80,
                            icon: "fa-server",
                          },
                        ].map((skill, index) => (
                          <div key={index} className="flex flex-col">
                            <div className="flex justify-between mb-2">
                              <div className="flex items-center">
                                <i
                                  className={`fas ${
                                    skill.icon ? skill.icon : "fa-code"
                                  } text-gray-400 mr-3`}
                                />
                                <span>{skill.name}</span>
                              </div>
                              {/* <span className="text-sm text-gray-400">
                                {skill.level}
                              </span> */}
                            </div>
                            <div className="h-2 w-full bg-gray-700 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gray-400 rounded-full"
                                style={{ width: `${skill.level}%` }}
                              ></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Languages & Frameworks */}
                    <div>
                      <h3 className="text-lg font-medium mb-6 mt-12">
                        {t("others")}
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-12  gap-y-4">
                        {[
                          {
                            name: "Korean",
                            level: "",
                            icon: "fa-language",
                            value: 100,
                          },
                          {
                            name: "English",
                            level: "",
                            icon: "fa-language",
                            value: 80,
                          },
                          {
                            name: "Spanish",
                            level: "",
                            icon: "fa-language",
                            value: 50,
                          },
                          {
                            name: "Japanese",
                            level: "",
                            icon: "fa-language",
                            value: 60,
                          },
                          {
                            name: "Piano",
                            level: "",
                            icon: "fa-solid fa-music",
                            value: 80,
                          },
                          {
                            name: "Drive",
                            level: "",
                            icon: "fa-solid fa-car",
                            value: 100,
                          },
                        ].map((skill, index) => (
                          <div key={index} className="flex flex-col">
                            <div className="flex justify-between mb-2">
                              <div className="flex items-center">
                                <i
                                  className={`fas ${skill.icon} text-gray-400 mr-3`}
                                />
                                <span>{skill.name}</span>
                              </div>
                              <span className="text-sm text-gray-400">
                                {skill.level}
                              </span>
                            </div>
                            <div className="h-2 w-full bg-gray-700 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gray-400 rounded-full"
                                style={{ width: `${skill.value}%` }}
                              ></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </Section>
            )}
          </section>
        </div>

        {showToast && (
          <div
            style={{
              position: "fixed",
              bottom: "20px",
              left: "50%",
              transform: "translateX(-50%)",
              background: "#333",
              color: "#fff",
              padding: "10px 20px",
              borderRadius: "8px",
              opacity: 0.9,
            }}
          >
            ✅ Email copied to clipboard!
          </div>
        )}
      </Body>
      <Footer />
    </>
  );
}

export default AboutContainer;
